import { createPortal } from 'react-dom';
import { useLoadingStore } from './loadingStore';
import { useDelayedFlag } from './useLoading';
import { LoaderSymbol } from './LoaderSymbol';

/**
 * Mounted ONCE at the app root (main.tsx).  Renders the app-wide loading UI:
 *  • a thin TOP PROGRESS BAR for any in-flight API call (determinate when a
 *    real progress value exists, indeterminate slide otherwise);
 *  • a BLOCKING SCRIM with the configured symbol + label/progress for
 *    app-level ops (save / validate / delete / export / report) so the user
 *    can't double-submit.
 * Both honour a 150ms flicker delay.  No per-form code needed.
 */
export function GlobalLoadingOverlay() {
  const active = useLoadingStore((s) => s.active) > 0;
  const blocking = useLoadingStore((s) => s.blocking) > 0;
  const progress = useLoadingStore((s) => s.progress);
  const label = useLoadingStore((s) => s.label);

  const showBar = useDelayedFlag(active, 150);
  const showScrim = useDelayedFlag(blocking, 150);

  return createPortal(
    <>
      {showBar && (
        <div
          className="ld-topbar"
          role="progressbar"
          aria-label="Loading"
          aria-valuenow={progress ?? undefined}
          aria-valuemin={progress == null ? undefined : 0}
          aria-valuemax={progress == null ? undefined : 100}
        >
          {progress == null ? (
            <div className="ld-topbar-ind" />
          ) : (
            <div className="ld-topbar-det" style={{ width: `${progress}%` }} />
          )}
        </div>
      )}

      {showScrim && (
        <div className="ld-scrim" role="status" aria-live="polite">
          <div className="ld-scrim-box">
            <LoaderSymbol size={36} />
            {label && <div className="ld-scrim-label">{label}</div>}
            {progress != null && (
              <>
                <div className="ld-scrim-bar"><div style={{ width: `${progress}%` }} /></div>
                <div className="ld-scrim-pct">{progress}%</div>
              </>
            )}
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
